/**
 * FromTripInvoiceDialog — generate a Trip-LINKED invoice.
 *
 * Unlike the ad-hoc ManualInvoiceDialog (which never carries a tripId), this
 * dialog always posts to `POST /api/invoices` with a real `tripId`, so the
 * resulting invoice is tagged with the trip's display id (TRV000001) and its
 * receivable is posted to the traveller's ledger.
 *
 * Props:
 *   open, onOpenChange   — controlled visibility
 *   presetTripId         — when set, the trip is pre-selected (the deep-link
 *                          from a trip's "Invoice" button passes this)
 *   onCreated(invoice)   — called with the created invoice on success
 *
 * Member-expression-free in JSX (filtered trip rows hoisted) to dodge the
 * visual-edits Babel plugin recursion bug.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FilePlus2, Loader2, Search, X, CheckCircle2, AlertTriangle, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

// FIX 1 (Task 3) — derive GST supply type from supplier state vs place of supply.
const normState = (s) => String(s || '').trim().toLowerCase().replace(/^\s*\d{1,2}\s*[-:.]?\s*/, '').replace(/[^a-z0-9]/g, '');
const deriveSupply = (supplierState, placeOfSupply) => {
  const a = normState(supplierState);
  const b = normState(placeOfSupply);
  if (!a || !b) return null;
  return a === b ? 'intrastate' : 'interstate';
};

// A trip split across several travellers raises one invoice per PAN, so the
// dialog has to say WHICH traveller it is billing. 'lead' keeps the historic
// behaviour (bill the trip's lead) and is what single-traveller trips always
// send — the key is then omitted from the payload entirely.
const LEAD = 'lead';

const initialForm = (tripId) => ({
  tripId: tripId || '',
  clientUcc: LEAD,
  invoiceType: 'tax',
  invoiceNumber: '',
  supplyType: 'interstate',
  placeOfSupply: '',
  tcsRate: '',
  dueDate: '',
  notes: '',
});

export default function FromTripInvoiceDialog({ open, onOpenChange, presetTripId, onCreated }) {
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState(initialForm(presetTripId));
  const [tripSearch, setTripSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [companyState, setCompanyState] = useState('');
  // BATCH 2 #1/#2 — money position for the selected trip (Total / Received /
  // Invoiced / Outstanding) so we never re-request an already-collected amount.
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  // Load trips + suggest a number on open. Seed the trip from presetTripId.
  useEffect(() => {
    if (!open) { setForm(initialForm()); setTripSearch(''); return; }
    let alive = true;
    axios.get(`${BACKEND_URL}/api/trips`, { withCredentials: true })
      .then((res) => {
        if (!alive) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.trips || []);
        setTrips(data);
      })
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/settings`, { withCredentials: true })
      .then((r) => { if (alive) setCompanyState(r.data?.state || ''); })
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/invoices/suggest-number?invoice_type=tax`, { withCredentials: true })
      .then((r) => { if (alive) setForm((p) => ({ ...p, invoiceNumber: p.invoiceNumber || r.data.invoiceNumber })); })
      .catch(() => {});
    setForm(initialForm(presetTripId));
    return () => { alive = false; };
  }, [open, presetTripId]);

  // FIX 1 — auto-derive supply type from place of supply vs the agency state.
  const derivedSupply = deriveSupply(companyState, form.placeOfSupply);
  useEffect(() => {
    if (derivedSupply && derivedSupply !== form.supplyType) {
      setForm((p) => ({ ...p, supplyType: derivedSupply }));
    }
  }, [derivedSupply, form.supplyType]);

  // BATCH 2 #1/#2 — fetch the trip's reconciliation whenever the selection
  // changes so the dialog shows what's already been received/invoiced.
  useEffect(() => {
    if (!open || !form.tripId) { setRecon(null); return; }
    let alive = true;
    setReconLoading(true);
    axios.get(`${BACKEND_URL}/api/invoices/reconciliation/${encodeURIComponent(form.tripId)}`, { withCredentials: true })
      .then((r) => { if (alive) setRecon(r.data); })
      .catch(() => { if (alive) setRecon(null); })
      .finally(() => { if (alive) setReconLoading(false); });
    return () => { alive = false; };
  }, [open, form.tripId]);

  const handleTypeChange = async (newType) => {
    let suggested = '';
    try {
      const r = await axios.get(`${BACKEND_URL}/api/invoices/suggest-number?invoice_type=${newType}`, { withCredentials: true });
      suggested = r.data.invoiceNumber;
    } catch { /* ignore */ }
    setForm((p) => ({ ...p, invoiceType: newType, invoiceNumber: suggested }));
  };

  const selectedTrip = useMemo(
    () => trips.find((t) => t.tripId === form.tripId) || null,
    [trips, form.tripId],
  );

  // Billable travellers on the selected trip: the lead plus any additionalPax
  // that has a UCC (a pax with no traveller record cannot be billed — there is
  // no PAN or address to put on the invoice). /api/trips returns whole trip
  // docs, so additionalPax is already here; no extra fetch.
  const travellerOptions = useMemo(() => {
    if (!selectedTrip) return [];
    const opts = [{
      value: LEAD,
      ucc: selectedTrip.clientUCC || '',
      label: selectedTrip.guestName || 'Lead traveller',
      display: selectedTrip.clientDisplayUCC || selectedTrip.clientUCC || '',
      isLead: true,
    }];
    const seen = new Set([selectedTrip.clientUCC || '']);
    for (const p of (selectedTrip.additionalPax || [])) {
      const ucc = (p.ucc || '').trim();
      if (!ucc || seen.has(ucc)) continue;
      seen.add(ucc);
      opts.push({
        value: ucc,
        ucc,
        label: p.name || ucc,
        display: p.displayUCC || ucc,
        isLead: false,
      });
    }
    return opts;
  }, [selectedTrip]);

  // A UCC picked for one trip is not a traveller on the next one (the backend
  // rejects that with a 400), so reset to the lead whenever the trip changes.
  useEffect(() => {
    setForm((p) => (p.clientUcc === LEAD ? p : { ...p, clientUcc: LEAD }));
  }, [form.tripId]);

  const filteredTrips = useMemo(() => {
    const q = tripSearch.trim().toLowerCase();
    const base = q
      ? trips.filter((t) =>
          (t.tripId || '').toLowerCase().includes(q) ||
          (t.displayTripId || '').toLowerCase().includes(q) ||
          (t.guestName || '').toLowerCase().includes(q) ||
          (t.clientUCC || '').toLowerCase().includes(q) ||
          (t.clientDisplayUCC || '').toLowerCase().includes(q) ||
          (t.destination || '').toLowerCase().includes(q))
      : trips;
    return base.slice(0, 30);
  }, [trips, tripSearch]);

  const tripResults = filteredTrips.map((t) => (
    <button
      type="button"
      key={t.tripId}
      onClick={() => { setForm((p) => ({ ...p, tripId: t.tripId })); setTripSearch(''); }}
      className="block w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-50 last:border-0"
      data-testid={`fromtrip-pick-${t.tripId}`}
    >
      <span className="font-mono font-semibold text-slate-800">{t.displayTripId || t.tripId}</span>
      {(t.clientDisplayUCC || t.clientUCC) && <span className="font-mono text-[10px] text-teal-600 ml-2">{t.clientDisplayUCC || t.clientUCC}</span>}
      <span className="text-slate-500 ml-2">{t.guestName || '—'}</span>
      <span className="text-slate-400 ml-2">{t.destination || ''}</span>
    </button>
  ));

  const handleCreate = async () => {
    if (!form.tripId) { toast.error('Please select a trip'); return; }
    if (!form.invoiceNumber.trim()) { toast.error('Invoice number is required'); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        tcsRate: form.tcsRate === '' ? null : Number(form.tcsRate),
      };
      // Only sent when billing a NON-lead traveller, so the request for a
      // single-traveller trip is byte-identical to what it has always been.
      if (form.clientUcc && form.clientUcc !== LEAD) {
        payload.clientUcc = form.clientUcc;
      } else {
        delete payload.clientUcc;
      }
      const res = await axios.post(`${BACKEND_URL}/api/invoices`, payload, { withCredentials: true });
      toast.success(`${form.invoiceType === 'proforma' ? 'Proforma' : 'Tax'} invoice ${res.data.invoiceNumber} created`);
      onOpenChange(false);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const locked = !!presetTripId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fromtrip-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus2 className="w-5 h-5 text-teal-600" />
            Invoice from trip
          </DialogTitle>
          <DialogDescription>
            Generates a {form.invoiceType === 'proforma' ? 'proforma' : 'tax'} invoice linked to the trip — totals + tax are pulled from the trip's pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Trip picker — locked chip when deep-linked from a trip, else search */}
          <div>
            <Label className="text-xs">Trip *</Label>
            {form.tripId ? (
              <div className="mt-1 flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-slate-50" data-testid="fromtrip-selected">
                <span className="font-mono text-xs font-semibold text-slate-800">
                  {(selectedTrip && (selectedTrip.displayTripId || selectedTrip.tripId)) || form.tripId}
                </span>
                {selectedTrip ? <span className="text-xs text-slate-500 truncate">{selectedTrip.guestName} · {selectedTrip.destination}</span> : null}
                {!locked ? (
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, tripId: '' }))}
                    className="ml-auto text-slate-400 hover:text-rose-600"
                    data-testid="fromtrip-change"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  value={tripSearch}
                  onChange={(e) => setTripSearch(e.target.value)}
                  placeholder="Search trip id, guest or destination…"
                  className="pl-8"
                  data-testid="fromtrip-search"
                />
                {tripSearch ? (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-56 overflow-auto" data-testid="fromtrip-results">
                    {tripResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">No trips match</div>
                    ) : tripResults}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Which traveller this invoice bills. Only rendered when the trip
              actually has a second billable traveller, so a single-traveller
              trip looks exactly as it did before. */}
          {form.tripId && travellerOptions.length > 1 ? (
            <div data-testid="fromtrip-traveller-wrap">
              <Label className="text-xs">Bill to traveller</Label>
              <Select value={form.clientUcc} onValueChange={(v) => setForm((p) => ({ ...p, clientUcc: v }))}>
                <SelectTrigger className="mt-1" data-testid="fromtrip-traveller"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {travellerOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`fromtrip-traveller-${t.value}`}>
                      {t.label}{t.display ? ` · ${t.display}` : ''}{t.isLead ? ' (lead)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1" data-testid="fromtrip-traveller-hint">
                The invoice carries this traveller's PAN, UCC and address, and the receivable posts to their ledger.
              </p>
            </div>
          ) : null}

          {/* BATCH 2 #1/#2 — payment position for the selected trip. */}
          {form.tripId && (
            <ReconSummary recon={recon} loading={reconLoading} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Invoice Type</Label>
              <Select value={form.invoiceType} onValueChange={handleTypeChange}>
                <SelectTrigger className="mt-1" data-testid="fromtrip-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tax">Tax Invoice</SelectItem>
                  <SelectItem value="proforma">Proforma Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Invoice Number *</Label>
              <Input
                value={form.invoiceNumber}
                onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                className="mt-1 font-mono"
                data-testid="fromtrip-number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Supply Type</Label>
              <Select value={form.supplyType} onValueChange={(v) => setForm((p) => ({ ...p, supplyType: v }))} disabled={!!derivedSupply}>
                <SelectTrigger className="mt-1" data-testid="fromtrip-supply"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interstate">Interstate (IGST)</SelectItem>
                  <SelectItem value="intrastate">Intrastate (CGST+SGST)</SelectItem>
                </SelectContent>
              </Select>
              {derivedSupply ? (
                <p className="text-[11px] text-teal-700 mt-1" data-testid="fromtrip-supply-auto">Auto-derived vs {companyState}</p>
              ) : null}
            </div>
            <div>
              <Label className="text-xs">TCS Rate</Label>
              <Select value={form.tcsRate} onValueChange={(v) => setForm((p) => ({ ...p, tcsRate: v }))}>
                <SelectTrigger className="mt-1" data-testid="fromtrip-tcs"><SelectValue placeholder="Auto (use trip)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No TCS (0%)</SelectItem>
                  <SelectItem value="2">TCS 2% (Flat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Place of Supply</Label>
              <Input value={form.placeOfSupply} onChange={(e) => setForm((p) => ({ ...p, placeOfSupply: e.target.value }))} className="mt-1" placeholder="e.g. Maharashtra" />
            </div>
            <div>
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={form.dueDate} onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))} className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="mt-1" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="fromtrip-submit">
            {creating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              `Generate ${form.invoiceType === 'proforma' ? 'Proforma' : 'Tax'} Invoice`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * BATCH 2 #1/#2 — compact money-position panel: Total (payable) / Received /
 * Invoiced / Outstanding. Never shows a negative outstanding — an
 * over-collection is flagged "Overpaid, needs review". A fully-paid trip shows
 * a clear PAID state instead of nudging for another receipt.
 */
function ReconSummary({ recon, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2.5 bg-slate-50" data-testid="fromtrip-recon-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking payments already received…
      </div>
    );
  }
  if (!recon) return null;

  const cell = (label, value, cls = 'text-slate-900') => (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className={`text-sm font-semibold ${cls}`}>{value}</span>
    </div>
  );

  // BATCH 2C — FX-mode trips: outstanding is tracked in the foreign currency.
  if (recon.currencyMode === 'FX') {
    const c = recon.currency || 'USD';
    const fx = (n) => `${c} ${Math.round(Number(n) || 0).toLocaleString()}`;
    return (
      <div className="border border-indigo-200 rounded-xl p-3 bg-indigo-50/50" data-testid="fromtrip-recon-fx">
        <div className="flex items-center gap-1.5 mb-2">
          <Wallet className="h-3.5 w-3.5 text-indigo-600" />
          <span className="text-xs font-semibold text-slate-700">FX position ({c})</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          {cell('Fixed price', fx(recon.payableForeign))}
          {cell('Received', fx(recon.receivedForeign), recon.receivedForeign > 0 ? 'text-emerald-700' : 'text-slate-900')}
          {cell('Outstanding', fx(recon.outstandingForeign), recon.outstandingForeign > 0 ? 'text-rose-700' : 'text-emerald-700')}
          {cell('INR collected', `₹${Math.round(recon.receivedInr || 0).toLocaleString('en-IN')}`, 'text-slate-700')}
        </div>
        <div className="mt-2.5 text-xs text-indigo-800 bg-indigo-100 border border-indigo-200 rounded-lg px-2.5 py-2">
          Indicative INR ₹{Math.round(recon.indicativeInr || 0).toLocaleString('en-IN')} — estimate only. INR is realised per-payment at that day's rate.
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid="fromtrip-recon">
      <div className="flex items-center gap-1.5 mb-2">
        <Wallet className="h-3.5 w-3.5 text-teal-600" />
        <span className="text-xs font-semibold text-slate-700">Payment position</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {cell('Total payable', fmtINR(recon.payable))}
        {cell('Received', fmtINR(recon.received), recon.received > 0 ? 'text-emerald-700' : 'text-slate-900')}
        {cell('Invoiced', fmtINR(recon.invoiced))}
        {recon.overpaid
          ? cell('Overpaid by', fmtINR(recon.overpaidBy), 'text-amber-700')
          : cell('Outstanding', fmtINR(recon.outstanding), recon.outstanding > 0 ? 'text-rose-700' : 'text-emerald-700')}
      </div>

      {recon.overpaid ? (
        <div className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-2" data-testid="fromtrip-recon-overpaid">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>Overpaid, needs review — received {fmtINR(recon.received)} against a payable of {fmtINR(recon.payable)}.</span>
        </div>
      ) : recon.status === 'paid' ? (
        <div className="mt-2.5 flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-lg px-2.5 py-2" data-testid="fromtrip-recon-paid">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span>Amount received {fmtINR(recon.received)} · Status: PAID — no further receipt needed.</span>
        </div>
      ) : recon.status === 'partial' ? (
        <div className="mt-2.5 text-xs text-slate-600" data-testid="fromtrip-recon-partial">
          Received {fmtINR(recon.received)} · Outstanding {fmtINR(recon.outstanding)}.
        </div>
      ) : recon.payable > 0 ? (
        <div className="mt-2.5 text-xs text-slate-600" data-testid="fromtrip-recon-unpaid">
          No payment received yet · full amount {fmtINR(recon.outstanding)} outstanding.
        </div>
      ) : null}
    </div>
  );
}
